import type { I18n } from "@lingui/core";
import { errorToastText } from "@/lib/translateBackendError";

// Backend error codes returned by the contract update endpoint.
// Keep in sync with internal/handlers/billing/contract_errors.go.
export const CONTRACT_ERROR_CODE = {
  ContractNotFound: "CONTRACT_NOT_FOUND",
  ContractTerminal: "CONTRACT_TERMINAL",
  DurationBlockedDraft: "CONTRACT_DURATION_BLOCKED_BY_DRAFT_INVOICE",
  DurationBlockedFinalized: "CONTRACT_DURATION_BLOCKED_BY_FINALIZED_INVOICE",
  DurationMultiVersion: "CONTRACT_DURATION_MULTI_VERSION",
  InvalidDates: "CONTRACT_INVALID_DATES",
  // 422 returned by every contract path that collides with a non-cancelled
  // invoice (schedule-change, update-version-period, cancel-version,
  // update-contract financial-field edit). Carries the index-aligned all_*
  // CSV arrays parsed by parseBlockingInvoicesError.
  EditBlockedByInvoices: "CONTRACT_EDIT_BLOCKED_BY_INVOICES",
  // 422 returned when a tariff-version assignment fails integrity validation
  // (unknown/deleted version, group-type mismatch, duplicate group type). The
  // ACTIVE-status check is relaxed on the edit path, so this only fires for a
  // genuinely invalid assignment.
  InvalidTariffVersion: "CONTRACT_INVALID_TARIFF_VERSION",
  // 422s of POST /contracts/versions/{id}/undo-change (issue #1623).
  UndoNotScheduleChangeCopy: "CONTRACT_UNDO_NOT_A_SCHEDULE_CHANGE_COPY",
  UndoFollowerMissing: "CONTRACT_UNDO_FOLLOWER_MISSING",
  UndoOverlappingVersion: "CONTRACT_UNDO_OVERLAPPING_VERSION",
} as const;

type BackendErrorBody = {
  message?: string;
  code?: string;
  errors?: Record<string, string>;
};

type ErrorWithBody = { body?: BackendErrorBody };

function extractBody(error: unknown): BackendErrorBody | undefined {
  if (error && typeof error === "object" && "body" in error) {
    const body = (error as ErrorWithBody).body;
    if (body && typeof body === "object") {
      return body;
    }
  }
  return undefined;
}

/**
 * Map a subsidy validation error (HTTP 400, body
 * `{ errors: { <field>: <reason> } }`) to a localized message. These errors
 * carry NO `code` field — the backend keys them on the field/reason pair (see
 * contract_service.go ContractBFFieldRequiredError /
 * ContractSubsidyMethodFieldError, surfaced in contract_handler.go). Returns
 * undefined when no subsidy field/reason pair matches so the caller can fall
 * through to its `code` switch / default.
 */
function getSubsidyFieldErrorMessage(
  errors: Record<string, string>,
  i18n: I18n,
): string | undefined {
  // Match on the (field, reason) pairs the backend emits. Order is irrelevant:
  // the backend only ever returns one subsidy field error at a time.
  for (const [field, reason] of Object.entries(errors)) {
    const key = `${field}:${reason}`;
    switch (key) {
      case "subsidy_office_id:required_when_subsidised":
        return i18n._({
          id: "contracts.errors.subsidy.officeRequiredWhenSubsidised",
          message: "A subsidy office is required for subsidised contracts.",
        });
      case "subsidy_office_id:required_when_subsidy_amount":
        return i18n._({
          id: "contracts.errors.subsidy.officeRequiredWhenAmount",
          message:
            "A subsidy office is required when a subvention amount is set.",
        });
      // Method-aware reasons (office calculation_method = zurich_bf | flat).
      case "beitragsfaktor:required_when_zurich_bf":
        return i18n._({
          id: "contracts.errors.subsidy.beitragsfaktorRequiredZurichBf",
          message:
            "A Beitragsfaktor is required for offices using the Zürich calculation method.",
        });
      case "subsidy_amount:forbidden_when_zurich_bf":
        return i18n._({
          id: "contracts.errors.subsidy.subsidyAmountForbiddenZurichBf",
          message:
            "A flat subsidy amount is not allowed for offices using the Zürich calculation method.",
        });
      case "subsidy_amount:required_when_flat":
        return i18n._({
          id: "contracts.errors.subsidy.subsidyAmountRequiredFlat",
          message:
            "A flat subsidy amount is required for offices using the flat calculation method.",
        });
      case "beitragsfaktor:forbidden_when_flat":
        return i18n._({
          id: "contracts.errors.subsidy.beitragsfaktorForbiddenFlat",
          message:
            "A Beitragsfaktor is not allowed for offices using the flat calculation method.",
        });
      // Subsidy case record (#2001): impossible date orders and the number width.
      case "beitragsfaktor_valid_from:after_valid_until":
        return i18n._({
          id: "contracts.errors.subsidyCase.bfValidFromAfterUntil",
          message: "The BF can't start after it expires. Check “BF valid from” and “BF valid until”.",
        });
      case "subsidy_decision_on:before_filed_on":
        return i18n._({
          id: "contracts.errors.subsidyCase.decisionBeforeFiling",
          message: "The decision date can't be before the request was filed.",
        });
      case "subsidy_decision_number:too_long":
        return i18n._({
          id: "contracts.errors.subsidyCase.decisionNumberTooLong",
          message: "The decision number can have at most 16 characters.",
        });
      case "subsidy_office_id:required_when_supplement_amount":
        return i18n._({
          id: "contracts.errors.subsidy.officeRequiredWhenSupplementAmount",
          message:
            "A subsidy office is required when a Subventionszuschuss is set.",
        });
      default:
        break;
    }
  }
  return undefined;
}

/**
 * Translate a backend error from the contract update endpoint into a
 * user-facing message. Falls back to the server-provided English message (or
 * a generic default) when the code is unknown.
 */
export function getContractUpdateErrorMessage(
  error: unknown,
  i18n: I18n,
  defaultMessage: string,
): string {
  const body = extractBody(error);
  const code = body?.code;
  const details = body?.errors ?? {};
  const period = details.period ?? "";
  const status = details.status ?? "";

  // Subsidy validation errors carry no `code`, only a field/reason errors map.
  // Map them first so they don't fall through to the raw English message.
  const subsidyMessage = getSubsidyFieldErrorMessage(details, i18n);
  if (subsidyMessage) {
    return subsidyMessage;
  }

  switch (code) {
    case CONTRACT_ERROR_CODE.ContractNotFound:
      return i18n._({
        id: "contracts.errors.update.notFound",
        message: "Contract not found.",
      });
    case CONTRACT_ERROR_CODE.ContractTerminal:
      return i18n._({
        id: "contracts.errors.update.terminal",
        message:
          "Cancelled or terminated contracts cannot be edited.",
      });
    case CONTRACT_ERROR_CODE.DurationBlockedDraft:
      return i18n._({
        id: "contracts.errors.update.durationBlockedDraft",
        message:
          "Cannot change the contract duration: a draft invoice exists for {period}. Cancel the draft invoice first, then adjust the contract.",
        values: { period },
      });
    case CONTRACT_ERROR_CODE.DurationBlockedFinalized:
      return i18n._({
        id: "contracts.errors.update.durationBlockedFinalized",
        message:
          "Cannot change the contract duration: a {status} invoice exists for {period}. Adjust this via a new contract version instead.",
        values: { period, status },
      });
    case CONTRACT_ERROR_CODE.DurationMultiVersion:
      return i18n._({
        id: "contracts.errors.update.durationMultiVersion",
        message:
          "This contract has multiple versions. Use the contract version editor to change individual version periods.",
      });
    case CONTRACT_ERROR_CODE.InvalidDates:
      return i18n._({
        id: "contracts.errors.update.invalidDates",
        message:
          "Contract dates are invalid: billing dates must fall within the contract duration and the end date must be after the start date.",
      });
    case CONTRACT_ERROR_CODE.InvalidTariffVersion:
      return i18n._({
        id: "contracts.errors.update.invalidTariffVersion",
        message:
          "The selected tariff version is invalid for this contract. Pick a valid tariff version for each group type and try again.",
      });
    default:
      // The backend's client-safe text, else the caller's default; never the
      // raw Error message (an HTTP status text for an ApiError).
      return errorToastText(error, i18n, defaultMessage);
  }
}

/** A single invoice that blocks a contract change (parsed from a 422). */
export interface BlockingInvoiceRef {
  id: string;
  period: string;
  status: string;
}

/** Structured collision data extracted from an invoice-collision 422. */
export interface BlockingInvoicesInfo {
  invoices: BlockingInvoiceRef[];
}

/**
 * Parse an invoice-collision 422 (from any contract change path) into the
 * structured list of blocking invoices the BlockingInvoicesDialog renders.
 *
 * Prefers the index-aligned CSV arrays (`all_invoice_ids` / `all_periods` /
 * `all_statuses`) which cover EVERY colliding invoice; falls back to the single
 * `invoice_id` / `period` / `status` keys when the arrays are absent. Returns
 * `null` when the error is not an invoice-collision so the caller can fall back
 * to the toast (`getContractUpdateErrorMessage`).
 */
export function parseBlockingInvoicesError(
  error: unknown,
): BlockingInvoicesInfo | null {
  const body = extractBody(error);
  const details = body?.errors;
  if (!details) {
    return null;
  }

  // Preferred path: index-aligned CSV arrays for all colliding invoices.
  if (details.all_invoice_ids) {
    const ids = details.all_invoice_ids.split(",").filter(Boolean);
    const periods = (details.all_periods ?? "").split(",");
    const statuses = (details.all_statuses ?? "").split(",");
    const invoices = ids.map((id, i) => ({
      id,
      period: periods[i] ?? "",
      status: statuses[i] ?? "",
    }));
    return invoices.length > 0 ? { invoices } : null;
  }

  // Fallback: single-invoice keys.
  if (details.invoice_id) {
    return {
      invoices: [
        {
          id: details.invoice_id,
          period: details.period ?? "",
          status: details.status ?? "",
        },
      ],
    };
  }

  return null;
}

/**
 * True when the error is an invoice-collision 422 that the component handles by
 * opening the BlockingInvoicesDialog. Mutation hooks use this in their `onError`
 * to suppress the generic red toast (the dialog is the user-facing surface) —
 * the promise still rejects so the awaiting component can open the dialog.
 */
export function isInvoiceCollisionError(error: unknown): boolean {
  return parseBlockingInvoicesError(error) !== null;
}
